/**
 * Cell Formatting Utilities
 * 
 * This module contains utilities for formatting cell values in the DataGrid component.
 */

import { LogicalType } from '@/lib/engine';
import { Column } from './types';
import { isDictionaryObject } from './dictionary-utils';
import { isFloatObject } from './type-utils';

// Cache for formatted values to improve performance
const MAX_CACHE_SIZE = 10000;
const formattedValueCache = new Map<string, string>();

/**
 * Extract value from a dictionary object using various methods
 */
export function extractDictionaryValue(value: any): string {
  // Try various methods to extract the value from a dictionary object
  
  // Method 1: Try to use the get() method
  if (typeof value.get === 'function') {
    try {
      const dictValue = value.get();
      return dictValue !== null && dictValue !== undefined 
        ? String(dictValue) 
        : '';
    } catch (e) {
      // Removed console.warn
    }
  }
  
  // Method 2: Try to access the value property
  if ('value' in value && value.value !== null && value.value !== undefined) {
    return String(value.value);
  }
  
  // Method 3: Try Apache Arrow specific dictionary structure
  if (value.dictionary && value.indices) {
    try {
      // Get the index from the indices vector
      const index = value.indices.get(0);
      // Get the value from the dictionary using the index
      const dictValue = value.dictionary.get(index);
      return dictValue !== null && dictValue !== undefined
        ? String(dictValue)
        : '';
    } catch (e) {
      // Removed console.warn
    }
  }
  
  // Method 4: Try toString method
  if (typeof value.toString === 'function') {
    try {
      const strValue = value.toString();
      // If toString doesn't just return the object type
      if (strValue && !strValue.includes('[object ')) {
        return strValue;
      }
    } catch (e) {
      // Removed console.warn
    }
  }
  
  // Method 5: Try to access the text property (used in some Arrow implementations)
  if ('text' in value && value.text !== null && value.text !== undefined) {
    return String(value.text);
  }
  
  // Method 6: Try to access the values property (used in some Arrow implementations)
  if (value.values && Array.isArray(value.values) && value.values.length > 0) {
    return String(value.values[0]);
  }
  
  // Fallback to empty string if all methods fail
  return '';
}

/**
 * Generate a cache key for formatted values
 */
function generateCacheKey(value: unknown, column: Column): string {
  // For primitive values, use a simple key
  if (value === null || value === undefined || typeof value !== 'object') {
    return `${column.name}:${String(value)}`;
  }
  
  // For objects, include type information in the key
  try {
    const valueType = typeof value;
    const columnType = column.type?.toString() || '';
    const logicalType = column.logicalType?.type || '';
    
    // For dictionary objects, try to include the actual value in the key
    if (isDictionaryObject(value)) {
      try {
        const anyValue = value as any;
        if (typeof anyValue.get === 'function') {
          const dictValue = anyValue.get();
          return `${column.name}:dict:${String(dictValue)}`;
        }
        if ('value' in anyValue) {
          return `${column.name}:dict:${String(anyValue.value)}`;
        }
      } catch (e) {
        // Ignore errors in cache key generation
      }
    }
    
    // Fallback to a more generic key
    return `${column.name}:${valueType}:${columnType}:${logicalType}`;
  } catch (e) {
    // Last resort fallback
    return `${column.name}:${String(value)}`;
  }
}

/**
 * Parameters for formatCellValue function
 */
export interface FormatCellValueParams {
  /** The value to format */
  value: unknown;
  /** The column definition containing type information */
  column: Column;
}

/**
 * Format cell value based on column type with caching for better performance
 */
export function formatCellValue({ value, column }: FormatCellValueParams): string {
  // Check cache first
  const cacheKey = generateCacheKey(value, column);
  if (formattedValueCache.has(cacheKey)) {
    return formattedValueCache.get(cacheKey)!;
  }
  
  // Removed console.log

  // Handle null/undefined values
  if (value === null || value === undefined) {
    // Even if the value is null/undefined, check if the column type indicates it's a dictionary
    // This handles cases where the column is dictionary-encoded but the value is null
    const columnTypeStr = String(column.type || '').toLowerCase();
    const mightBeDictionary = columnTypeStr.includes('dictionary') || 
                             columnTypeStr.includes('utf8') ||
                             columnTypeStr.includes('string');
    
    // Removed console.log
    
    // Return empty string for null/undefined values
    return '';
  }

  let formattedValue = '';
  
  try {
    // Special handling for dictionary-encoded columns
    // Check if the column type indicates it might be a dictionary
    const columnTypeStr = String(column.type || '').toLowerCase();
    const mightBeDictionary = columnTypeStr.includes('dictionary') || 
                             columnTypeStr.includes('utf8') ||
                             columnTypeStr.includes('string');
    
    // For date columns, try to handle date strings and timestamps
    if (column.logicalType?.type === LogicalType.DATE || 
        column.logicalType?.type === LogicalType.TIMESTAMP) {
      
      // If it's already a Date object
      if (value instanceof Date) {
        formattedValue = value.toLocaleString();
      }
      // If it's a timestamp number
      else if (typeof value === 'number') {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          formattedValue = date.toLocaleString();
        }
      }
      // If it's a date string
      else if (typeof value === 'string') {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          formattedValue = date.toLocaleString();
        } else {
          formattedValue = value; // Return the original string if parsing fails
        }
      }
    }
    // For currency columns
    else if (column.logicalType?.type === LogicalType.CURRENCY && typeof value === 'number') {
      formattedValue = new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: 'USD',
        minimumFractionDigits: column.logicalType.precision || 2
      }).format(value);
    }
    // For percentage columns
    else if (column.logicalType?.type === LogicalType.PERCENTAGE && typeof value === 'number') {
      formattedValue = new Intl.NumberFormat('en-US', { 
        style: 'percent',
        minimumFractionDigits: column.logicalType.precision || 0
      }).format(value / 100);
    }
    // Handle basic types
    else if (typeof value === 'string') {
      formattedValue = value;
    }
    else if (typeof value === 'number') {
      formattedValue = isNaN(value) ? '' : value.toString();
    }
    else if (typeof value === 'boolean') {
      formattedValue = value ? 'Yes' : 'No';
    }
    // Handle Float objects from Apache Arrow
    else if (isFloatObject(value)) {
      const anyValue = value as any;
      if ('value' in anyValue && typeof anyValue.value === 'number') {
        formattedValue = isNaN(anyValue.value) ? '' : anyValue.value.toString();
      } else {
        formattedValue = String(value);
      }
    }
    // Handle Dictionary objects from Apache Arrow
    else if (isDictionaryObject(value) || (typeof value === 'object' && mightBeDictionary)) {
      // If the value is null or undefined but the column type indicates it's a dictionary,
      // return an empty string
      if (value === null || value === undefined) {
        return '';
      }
      
      const anyValue = value as any;
      
      // Use the extractDictionaryValue helper function
      formattedValue = extractDictionaryValue(anyValue);
      
      // Removed console.warn
    }
    // Handle other objects
    else if (typeof value === 'object') {
      try {
        formattedValue = JSON.stringify(value);
      } catch (err) {
        formattedValue = String(value);
      }
    }
    // Final fallback
    else {
      formattedValue = String(value);
    }
  } catch (err) {
    // Removed console.error
    
    // Last resort fallback - return the value as a string if possible
    try {
      formattedValue = String(value);
    } catch (e) {
      formattedValue = '';
    }
  }
  
  // Store in cache if we have a valid string
  if (formattedValueCache.size >= MAX_CACHE_SIZE) {
    // If cache is full, clear the oldest 20% of entries
    const keysToDelete = Array.from(formattedValueCache.keys()).slice(0, MAX_CACHE_SIZE / 5);
    keysToDelete.forEach(key => formattedValueCache.delete(key));
  }
  
  formattedValueCache.set(cacheKey, formattedValue);
  return formattedValue;
}

/**
 * Parameters for safeToString function
 */
export interface SafeToStringParams {
  /** The value to convert to string */
  value: unknown;
  /** Optional column definition for better formatting */
  column?: Column;
}

/**
 * Convert any value to a safe string representation for rendering
 */
export function safeToString({ value, column }: SafeToStringParams): string {
  if (value === null || value === undefined) {
    return '';
  }
  
  // If we have column information, use formatCellValue for better formatting
  if (column) {
    const result = formatCellValue({ value, column });
    return result;
  }
  
  // Otherwise, use basic formatting
  if (isFloatObject(value)) {
    try {
      const anyValue = value as any;
      if ('value' in anyValue && typeof anyValue.value === 'number') {
        const result = anyValue.value.toString();
        return result;
      }
      const result = anyValue.toString();
      return result;
    } catch (err) {
      const result = String(value);
      return result;
    }
  }
  
  let result: string;
  
  try {
    // Try to convert to string using String constructor
    result = String(value);
  } catch (err) {
    // If even String() fails, return a fallback
    result = '[Error: Unable to convert to string]';
  }
  
  // Final safety check - if result is not a string (which shouldn't happen, but just in case),
  // return a hardcoded string
  if (typeof result !== 'string') {
    return '[Error: Non-string result]';
  }
  
  return result;
}

/**
 * Safely format a row count value to a string, handling various types including Float objects
 * 
 * @param rowCount - The row count value to format (can be a number, Float object, or other type)
 * @returns A formatted string representation of the row count
 */
export function formatRowCount(rowCount: any): string {
  try {
    // Handle null/undefined
    if (rowCount == null) {
      return '0 rows';
    }
    
    // Handle regular numbers
    if (typeof rowCount === 'number') {
      return `${rowCount.toLocaleString()} rows`;
    }
    
    // Handle Float objects (with value property)
    if (typeof rowCount === 'object') {
      // Try to access value property first (common in Float objects)
      if ('value' in rowCount && rowCount.value != null) {
        const value = rowCount.value;
        return `${typeof value === 'number' ? value.toLocaleString() : String(value)} rows`;
      }
      
      // Try toString method
      if (typeof rowCount.toString === 'function') {
        const str = rowCount.toString();
        if (str !== '[object Object]') return `${str} rows`;
      }
    }
    
    // Final fallback
    return `${String(rowCount)} rows`;
  } catch (err) {
    return 'Unknown rows';
  }
} 